/** THE ENTRY BLOCK — the first thing on "Tonight's game".
 *
 *  Three doors for a phone nobody knows yet (guest / log in / register), and
 *  a welcome line once the phone is signed in. A tap signs NOBODY in.
 *
 *  Everything here goes through the account form the fan card already uses:
 *  POST /patron/public/{card}/account and /sign-out. No new account, no
 *  second PIN — the same email + PIN works at the venue on any future night
 *  and from the emailed "Play from anywhere" link.
 */
import { useState } from 'react';
import { BellRing, Coins, Loader2, LogOut, UserPlus } from 'lucide-react';
import { GoalReminderChoice } from './GoalReminderChoice';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/patron`;
const DEVICE_KEY = 'patron_device_id';
const GUEST_KEY = 'goal_guest_mode';

const deviceId = () => {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
        id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
        localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
};

/** Under 10,000 the whole number with a separator; from 10,000 in K with at
 *  most one decimal — 4,500 · 12.5K · 136K. The browser never adds points up,
 *  it only prints the balance the server sent. */
export const shortPoints = (value) => {
    const n = Number(value || 0);
    if (n < 10000) return n.toLocaleString();
    const k = n / 1000;
    const text = k.toFixed(1).replace(/\.0$/, '');
    return `${text}K`;
};

export const GoalEntryBlock = ({ cardId, lang = 'en', t, badge, onChanged,
                                c, ty, primaryBtn, ghostBtn, pointsColour }) => {
    // 'choices' | 'login' | 'register' | 'guest' | 'reminders'
    const [step, setStep] = useState(
        () => (sessionStorage.getItem(`${GUEST_KEY}:${cardId}`) ? 'guest' : 'choices'));
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [pin, setPin] = useState('');
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // ONE PLAYER PER PHONE — who this phone already is, so a second sign-up is
    // offered that player instead of an error.
    const [boundPlayer, setBoundPlayer] = useState('');
    // GAME REMINDERS — optional, for a new account and an existing one alike.
    const [channel, setChannel] = useState(badge?.reminder_channel || 'email');
    const [mobile, setMobile] = useState('');
    const [smsOk, setSmsOk] = useState(!!badge?.reminder_sms_consent);

    if (!badge?.enabled) return null;

    const box = { backgroundColor: 'rgba(255,255,255,0.06)', color: c.text };
    const input = 'w-full rounded-lg border border-white/20 bg-black/20 px-3 py-2 text-base';
    const action = 'w-full min-h-[44px] rounded-xl font-bold py-3 shadow-lg disabled:opacity-50';
    const quiet = 'w-full min-h-[44px] rounded-xl border border-white/25 py-3 text-sm font-semibold';
    const errorText = {
        pin_four_digits: t('patron.errorPin'),
        consent_required: t('patron.errorConsent'),
        mismatch: t('patron.errorPinWrong'),
        locked: t('patron.errorLocked'),
        email_required: t('patron.errorGeneric'),
        mobile_not_valid: t('goalGame.reminders.errorMobile'),
        mobile_taken: t('goalGame.reminders.errorMobileTaken'),
        phone_taken: t('goalGame.phoneTakenBody'),
    }[error] || (error ? t('patron.errorGeneric') : '');

    /** This phone is already a player tonight — carry on as that player. No
     *  PIN: the binding on this device is the proof. */
    const continueAsBound = async () => {
        setBusy(true);
        try {
            const res = await fetch(`${API}/public/${cardId}/continue-as-bound`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ device_id: deviceId() }),
            });
            if (res.ok) {
                const out = await res.json();
                setBoundPlayer(''); setError(''); setStep('choices');
                onChanged?.(out);
            }
        } catch (_e) { /* the message stays on screen */ }
        finally { setBusy(false); }
    };

    const continueAsBlock = boundPlayer ? (
        <button type="button" onClick={continueAsBound} disabled={busy}
                style={primaryBtn} className={action} data-testid="goal-entry-continue-as">
            {t('goalGame.continueAs', { player: boundPlayer.trim() })}
        </button>
    ) : null;

    const wantsSms = channel === 'sms' || channel === 'both';

    const post = async (path, body) => {
        setBusy(true);
        setError('');
        try {
            const res = await fetch(`${API}/public/${cardId}/${path}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...body, device_id: deviceId(), lang }),
            });
            const out = await res.json();
            if (!res.ok) {
                const detail = out.detail;
                if (detail && typeof detail === 'object' && detail.reason === 'phone_taken') {
                    setBoundPlayer(detail.player || ' ');
                }
                setError((detail && detail.reason) || detail || 'error');
                return null;
            }
            onChanged?.(out);
            setEmail(''); setName(''); setPin(''); setConsent(false); setMobile('');
            setStep('choices');
            return out;
        } catch (_e) { setError('error'); return null; }
        finally { setBusy(false); }
    };

    /** The three reminder pills, the mobile box and the SMS tick. Shown on the
     *  register form and on its own for a player who already has an account. */
    const reminderBlock = (
        <GoalReminderChoice t={t} channel={channel} onChannel={setChannel}
                            mobile={mobile} onMobile={setMobile}
                            smsOk={smsOk} onSmsOk={setSmsOk}
                            smsText={badge.sms_consent_text}
                            mobileMasked={badge.reminder_mobile_masked}
                            primaryBtn={primaryBtn} ghostBtn={ghostBtn} />
    );

    const reminderPayload = () => ({
        reminder_channel: channel,
        ...(wantsSms ? { mobile, sms_consent: smsOk } : {}),
    });

    // ── signed in: welcome back + the live balance
    if (badge.signed_in) {
        const savedKey = { email: 'savedEmail', sms: 'savedSms', both: 'savedBoth' }[
            badge.reminder_channel || 'email'] || 'savedEmail';
        return (
            <div className="rounded-2xl p-4 mb-4 text-left" style={box} data-testid="goal-entry-welcome">
                <p className="text-base font-bold" style={ty.headline} data-testid="goal-entry-welcome-name">
                    {badge.screen_name
                        ? t('goalGame.entry.welcomeName', { name: badge.screen_name })
                        : t('goalGame.entry.welcome')}
                </p>
                <p className="text-sm font-semibold tabular-nums mt-0.5"
                   style={{ color: pointsColour, textShadow: '0 1px 2px rgba(0,0,0,0.55)' }}
                   data-testid="goal-entry-balance">
                    {t('goalGame.entry.balance', { points: shortPoints(badge.points) })}
                </p>
                {/* GAME REMINDERS — optional for a player who already has an
                    account: what they have now, and one tap to change it. */}
                {step === 'reminders' ? (
                    <div className="mt-3 space-y-2" data-testid="goal-reminder-form">
                        {reminderBlock}
                        <button type="button" style={primaryBtn} className={action}
                                disabled={busy || (wantsSms && !mobile.trim() && !badge.reminder_mobile_masked)}
                                data-testid="goal-reminder-save"
                                onClick={() => post('reminders', reminderPayload())}>
                            {busy ? <Loader2 className="animate-spin mx-auto" size={18} />
                                  : t('goalGame.reminders.save')}
                        </button>
                        <button type="button" onClick={() => { setError(''); setStep('choices'); }}
                                className="w-full text-xs underline opacity-70 min-h-[44px]"
                                data-testid="goal-reminder-cancel">{t('goalGame.back')}</button>
                    </div>
                ) : (
                    <>
                        <p className="text-[11px] opacity-70 mt-2" data-testid="goal-reminder-state">
                            {t(`goalGame.reminders.${savedKey}`, {
                                phone: badge.reminder_mobile_masked || '' })}
                        </p>
                        <button type="button" onClick={() => { setError(''); setStep('reminders'); }}
                                data-testid="goal-reminder-open"
                                className="mt-1 inline-flex items-center gap-1.5 text-xs underline opacity-70 min-h-[44px]">
                            <BellRing size={12} />{t('goalGame.reminders.change')}
                        </button>
                    </>
                )}
                <button type="button" onClick={() => post('sign-out', {})} disabled={busy}
                        data-testid="goal-entry-sign-out"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs underline opacity-70 min-h-[44px]">
                    {busy ? <Loader2 className="animate-spin" size={12} /> : <LogOut size={12} />}
                    {t('goalGame.entry.notYou')}
                </button>
                {errorText && <p className="text-sm text-red-300 mt-1" data-testid="goal-entry-error">{errorText}</p>}
            </div>
        );
    }

    // ── a guest changed nothing: one thin line, so they can still come back
    if (step === 'guest') {
        return (
            <div className="rounded-2xl px-4 py-3 mb-4 flex items-center justify-between gap-3 text-left"
                 style={box} data-testid="goal-entry-guest-line">
                <span className="text-xs opacity-70">{t('goalGame.entry.guestPlaying')}</span>
                <span className="flex items-center gap-3 shrink-0">
                    <button type="button" onClick={() => setStep('login')} data-testid="goal-entry-guest-login"
                            className="text-xs font-semibold underline min-h-[44px]">
                        {t('goalGame.entry.login')}
                    </button>
                    <button type="button" onClick={() => setStep('register')} data-testid="goal-entry-guest-register"
                            className="text-xs font-semibold underline min-h-[44px]">
                        {t('goalGame.entry.registerShort')}
                    </button>
                </span>
            </div>
        );
    }

    const program = badge.program_name || '';

    return (
        <div className="rounded-2xl p-4 mb-4 text-left" style={box} data-testid="goal-entry-block">
            {step === 'choices' && (
                <div className="space-y-2" data-testid="goal-entry-choices">
                    <button type="button" style={ghostBtn} className={quiet}
                            data-testid="goal-entry-guest"
                            onClick={() => {
                                sessionStorage.setItem(`${GUEST_KEY}:${cardId}`, '1');
                                setStep('guest');
                            }}>
                        {t('goalGame.entry.guest')}
                    </button>
                    <button type="button" style={ghostBtn} className={quiet}
                            data-testid="goal-entry-login-open"
                            onClick={() => { setError(''); setStep('login'); }}>
                        {t('goalGame.entry.login')}
                    </button>
                    <button type="button" style={primaryBtn} className={action}
                            data-testid="goal-entry-register-open"
                            onClick={() => { setError(''); setStep('register'); }}>
                        <UserPlus size={14} className="inline mr-1.5 -mt-0.5" />
                        {t('goalGame.entry.register', { program })}
                    </button>
                </div>
            )}

            {step === 'login' && (
                <div className="space-y-2" data-testid="goal-entry-login">
                    <p className="text-sm font-bold" style={ty.headline}>{t('goalGame.entry.login')}</p>
                    <input type="email" value={email} inputMode="email" autoComplete="email"
                           onChange={(e) => setEmail(e.target.value)} className={input}
                           placeholder={t('patron.emailPlaceholder')} data-testid="goal-entry-login-email" />
                    <input type="text" value={pin} inputMode="numeric" maxLength={4}
                           onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                           className={`${input} tracking-[0.4em]`}
                           placeholder={t('patron.pinPlaceholder')} data-testid="goal-entry-login-pin" />
                    <button type="button" style={primaryBtn} className={action}
                            disabled={busy || !email || pin.length !== 4}
                            data-testid="goal-entry-login-submit"
                            onClick={() => post('account', { email, pin })}>
                        {busy ? <Loader2 className="animate-spin mx-auto" size={18} />
                              : t('goalGame.entry.login')}
                    </button>
                    <button type="button" onClick={() => { setError(''); setStep('choices'); }}
                            className="w-full text-xs underline opacity-70 min-h-[44px]"
                            data-testid="goal-entry-login-cancel">{t('goalGame.back')}</button>
                    {errorText && <p className="text-sm text-red-300" data-testid="goal-entry-error">{errorText}</p>}
                    {continueAsBlock}
                </div>
            )}

            {step === 'register' && (
                <div className="space-y-2" data-testid="goal-entry-register">
                    <p className="text-sm font-bold" style={ty.headline}>
                        <Coins size={14} className="inline mr-1.5 -mt-0.5" />
                        {t('goalGame.entry.register', { program })}
                    </p>
                    <p className="text-xs opacity-70" data-testid="goal-entry-register-note">
                        {t('goalGame.entry.registerNote')}
                    </p>
                    <input type="text" value={name} maxLength={24}
                           onChange={(e) => setName(e.target.value)} className={input}
                           placeholder={t('patron.namePlaceholder')} data-testid="goal-entry-register-name" />
                    <input type="email" value={email} inputMode="email" autoComplete="email"
                           onChange={(e) => setEmail(e.target.value)} className={input}
                           placeholder={t('patron.emailPlaceholder')} data-testid="goal-entry-register-email" />
                    <input type="text" value={pin} inputMode="numeric" maxLength={4}
                           onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                           className={`${input} tracking-[0.4em]`}
                           placeholder={t('patron.pinPlaceholder')} data-testid="goal-entry-register-pin" />
                    <label className="flex items-start gap-2 text-[11px] opacity-80">
                        <input type="checkbox" checked={consent} data-testid="goal-entry-register-consent"
                               onChange={(e) => setConsent(e.target.checked)}
                               className="mt-0.5 h-4 w-4 rounded border-white/30" />
                        <span>{badge.consent_text}</span>
                    </label>
                    <p className="text-[10px] opacity-60">{badge.consent_note}</p>
                    {reminderBlock}
                    <button type="button" style={primaryBtn} className={action}
                            disabled={busy || !email || pin.length !== 4 || !name.trim()
                                      || (wantsSms && !mobile.trim())}
                            data-testid="goal-entry-register-submit"
                            onClick={() => post('account', { email, pin, consent, screen_name: name,
                                                            ...reminderPayload() })}>
                        {busy ? <Loader2 className="animate-spin mx-auto" size={18} />
                              : t('goalGame.entry.registerSubmit')}
                    </button>
                    <button type="button" onClick={() => { setError(''); setStep('choices'); }}
                            className="w-full text-xs underline opacity-70 min-h-[44px]"
                            data-testid="goal-entry-register-cancel">{t('goalGame.back')}</button>
                    {errorText && <p className="text-sm text-red-300" data-testid="goal-entry-error">{errorText}</p>}
                    {continueAsBlock}
                </div>
            )}
        </div>
    );
};
